import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '../../mocks/blogPosts';
import { useSEO, generateBlogPostingSchema } from '../../utils/seo';
import BlogNavbar from '../blog/components/BlogNavbar';
import BlogFooter from '../blog/components/BlogFooter';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [scrolled, setScrolled] = useState(false);

  const post = blogPosts.find(p => p.slug === slug);

  // SEO Configuration - moved outside useEffect
  const publishDate = post ? new Date(post.date.split('/').reverse().join('-')).toISOString() : new Date().toISOString();
  
  useSEO({
    title: post ? `${post.title} | Blog NeuroAgents` : 'Blog NeuroAgents',
    description: post?.excerpt || 'Blog sobre Inteligência Artificial e Automação',
    keywords: post ? `${post.category.toLowerCase()}, inteligência artificial, automação, atendimento, ${post.title.toLowerCase()}` : 'inteligência artificial, automação, atendimento',
    canonical: post ? `/blog/${post.slug}` : '/blog',
    type: 'article',
    image: post?.image,
    article: post ? {
      publishedTime: publishDate,
      modifiedTime: publishDate,
      author: post.author,
      section: post.category,
      tags: [post.category, 'IA', 'Automação', 'Atendimento'],
    } : undefined,
    jsonLd: post ? generateBlogPostingSchema({
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: publishDate,
      dateModified: publishDate,
      author: post.author,
      url: `/blog/${post.slug}`,
    }) : undefined,
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!post) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Artigo não encontrado</h1>
          <Link to="/blog" className="text-accent-green hover:underline cursor-pointer">
            Voltar ao Blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-black text-white">
      <BlogNavbar scrolled={scrolled} />

      {/* Hero Image */}
      <div className="relative h-[60vh] min-h-[500px] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="mb-4">
              <span className={`${post.categoryColor} text-black px-4 py-2 rounded-full text-sm font-bold`}>
                {post.category}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-gray-300">
              <div className="flex items-center gap-3">
                <img
                  src={post.authorAvatar}
                  alt={post.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <span className="font-medium">{post.author}</span>
              </div>
              <span>•</span>
              <span>{post.date}</span>
              <span>•</span>
              <div className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-16 bg-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Excerpt */}
          <div className="text-xl text-gray-300 mb-12 pb-12 border-b border-dark-border">
            {post.excerpt}
          </div>

          {/* Content based on post ID */}
          <div className="prose prose-invert prose-lg max-w-none">
            {post.id === 1 && (
              <>
                <h2 className="text-3xl font-bold text-white mb-6">A Revolução da IA no Atendimento</h2>
                <p className="text-gray-300 mb-6">
                  A inteligência artificial está transformando radicalmente a forma como as empresas se relacionam com seus clientes. Em 2025, não se trata mais de uma tendência futurista, mas de uma realidade presente que está redefinindo os padrões de excelência no atendimento ao cliente.
                </p>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Por Que a IA é Essencial Agora?</h3>
                <p className="text-gray-300 mb-6">
                  Os clientes de hoje esperam respostas instantâneas, personalizadas e disponíveis 24/7. Estudos recentes mostram que 73% dos consumidores consideram a experiência do cliente tão importante quanto a qualidade do produto ou serviço oferecido.
                </p>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Principais Benefícios da IA no Atendimento</h3>
                <ul className="list-disc list-inside text-gray-300 mb-6 space-y-3">
                  <li><strong className="text-white">Disponibilidade 24/7:</strong> Atendimento ininterrupto sem custos adicionais com turnos noturnos</li>
                  <li><strong className="text-white">Respostas Instantâneas:</strong> Tempo médio de resposta reduzido de minutos para segundos</li>
                  <li><strong className="text-white">Personalização em Escala:</strong> Cada cliente recebe atendimento personalizado baseado em seu histórico</li>
                  <li><strong className="text-white">Redução de Custos:</strong> Economia de até 60% nos custos operacionais de atendimento</li>
                  <li><strong className="text-white">Aumento da Satisfação:</strong> Empresas reportam aumento de até 300% na satisfação do cliente</li>
                </ul>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Casos de Sucesso Reais</h3>
                <p className="text-gray-300 mb-6">
                  Empresas brasileiras que implementaram IA no atendimento estão colhendo resultados impressionantes. Uma rede de varejo nacional reduziu o tempo médio de atendimento de 8 minutos para 45 segundos, enquanto uma fintech aumentou sua taxa de resolução no primeiro contato de 45% para 89%.
                </p>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Como Começar?</h3>
                <p className="text-gray-300 mb-6">
                  A implementação de IA no atendimento não precisa ser complexa. Comece identificando os pontos de dor mais comuns dos seus clientes e automatize essas interações primeiro. Expanda gradualmente conforme ganha confiança e coleta dados sobre o comportamento dos usuários.
                </p>

                <div className="bg-dark-secondary p-8 rounded-2xl mt-8">
                  <h4 className="text-xl font-bold text-accent-green mb-4">💡 Dica Prática</h4>
                  <p className="text-gray-300">
                    Comece com um piloto em um canal específico (como WhatsApp) antes de expandir para todos os canais de atendimento. Isso permite ajustes e aprendizados sem impactar toda a operação.
                  </p>
                </div>
              </>
            )}

            {post.id === 2 && (
              <>
                <h2 className="text-3xl font-bold text-white mb-6">O Que é Automação de Processos?</h2>
                <p className="text-gray-300 mb-6">
                  Automação de processos é a utilização de tecnologia para executar tarefas repetitivas e baseadas em regras com mínima intervenção humana. Em 2025, a automação inteligente vai além das regras simples, incorporando IA para tomar decisões complexas.
                </p>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Tipos de Automação</h3>
                <div className="space-y-6 mb-8">
                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <h4 className="text-xl font-bold text-accent-green mb-3">1. Automação Básica (RPA)</h4>
                    <p className="text-gray-300">
                      Robotic Process Automation executa tarefas repetitivas seguindo regras pré-definidas. Ideal para processos estruturados como entrada de dados e geração de relatórios.
                    </p>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <h4 className="text-xl font-bold text-accent-green mb-3">2. Automação Inteligente (IPA)</h4>
                    <p className="text-gray-300">
                      Intelligent Process Automation combina RPA com IA para lidar com processos não estruturados, aprender com dados e tomar decisões complexas.
                    </p>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <h4 className="text-xl font-bold text-accent-green mb-3">3. Hiperautomação</h4>
                    <p className="text-gray-300">
                      Abordagem holística que combina múltiplas tecnologias (IA, ML, RPA) para automatizar processos de ponta a ponta em toda a organização.
                    </p>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Passo a Passo para Implementação</h3>
                <ol className="list-decimal list-inside text-gray-300 mb-6 space-y-4">
                  <li className="pl-2">
                    <strong className="text-white">Mapeamento de Processos:</strong> Identifique e documente todos os processos candidatos à automação
                  </li>
                  <li className="pl-2">
                    <strong className="text-white">Priorização:</strong> Avalie cada processo por impacto, complexidade e ROI potencial
                  </li>
                  <li className="pl-2">
                    <strong className="text-white">Prova de Conceito:</strong> Implemente um piloto em processo de baixo risco
                  </li>
                  <li className="pl-2">
                    <strong className="text-white">Medição de Resultados:</strong> Estabeleça KPIs e monitore o desempenho
                  </li>
                  <li className="pl-2">
                    <strong className="text-white">Expansão Gradual:</strong> Escale para outros processos baseado nos aprendizados
                  </li>
                </ol>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">ROI Esperado</h3>
                <p className="text-gray-300 mb-6">
                  Empresas que implementam automação inteligente reportam redução de 60% nos custos operacionais, aumento de 40% na produtividade e ROI positivo em média de 6-12 meses.
                </p>
              </>
            )}

            {post.id === 3 && (
              <>
                <h2 className="text-3xl font-bold text-white mb-6">Entendendo os Chatbots</h2>
                <p className="text-gray-300 mb-6">
                  Nem todos os chatbots são criados iguais. Entender as diferenças fundamentais entre chatbots tradicionais e inteligentes é crucial para escolher a solução certa para seu negócio.
                </p>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Chatbots Tradicionais (Baseados em Regras)</h3>
                <div className="bg-dark-secondary p-6 rounded-xl mb-6">
                  <h4 className="text-lg font-bold text-white mb-3">Características:</h4>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li>Seguem fluxos de conversação pré-definidos</li>
                    <li>Reconhecem apenas palavras-chave específicas</li>
                    <li>Não aprendem com as interações</li>
                    <li>Limitados a cenários programados</li>
                    <li>Baixo custo de implementação</li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-dark-border">
                    <p className="text-amber-500 font-medium">✓ Melhor para: FAQs simples, processos lineares</p>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Chatbots Inteligentes (IA Generativa)</h3>
                <div className="bg-dark-secondary p-6 rounded-xl mb-6">
                  <h4 className="text-lg font-bold text-white mb-3">Características:</h4>
                  <ul className="list-disc list-inside text-gray-300 space-y-2">
                    <li>Compreendem contexto e intenção</li>
                    <li>Geram respostas naturais e personalizadas</li>
                    <li>Aprendem continuamente com interações</li>
                    <li>Lidam com perguntas complexas e ambíguas</li>
                    <li>Mantêm contexto ao longo da conversa</li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-dark-border">
                    <p className="text-accent-green font-medium">✓ Melhor para: Atendimento complexo, vendas consultivas</p>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Comparação Prática</h3>
                <div className="overflow-x-auto mb-8">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-dark-border">
                        <th className="py-4 px-4 text-white font-bold">Aspecto</th>
                        <th className="py-4 px-4 text-white font-bold">Tradicional</th>
                        <th className="py-4 px-4 text-white font-bold">Inteligente</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      <tr className="border-b border-dark-border">
                        <td className="py-4 px-4 font-medium">Compreensão</td>
                        <td className="py-4 px-4">Palavras-chave</td>
                        <td className="py-4 px-4 text-accent-green">Contexto e intenção</td>
                      </tr>
                      <tr className="border-b border-dark-border">
                        <td className="py-4 px-4 font-medium">Flexibilidade</td>
                        <td className="py-4 px-4">Baixa</td>
                        <td className="py-4 px-4 text-accent-green">Alta</td>
                      </tr>
                      <tr className="border-b border-dark-border">
                        <td className="py-4 px-4 font-medium">Aprendizado</td>
                        <td className="py-4 px-4">Não aprende</td>
                        <td className="py-4 px-4 text-accent-green">Contínuo</td>
                      </tr>
                      <tr className="border-b border-dark-border">
                        <td className="py-4 px-4 font-medium">Custo Inicial</td>
                        <td className="py-4 px-4 text-accent-green">Baixo</td>
                        <td className="py-4 px-4">Médio</td>
                      </tr>
                      <tr>
                        <td className="py-4 px-4 font-medium">ROI Longo Prazo</td>
                        <td className="py-4 px-4">Limitado</td>
                        <td className="py-4 px-4 text-accent-green">Alto</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Qual Escolher?</h3>
                <p className="text-gray-300 mb-6">
                  A escolha depende da complexidade das suas interações e objetivos de negócio. Para atendimento simples e orçamento limitado, chatbots tradicionais podem ser suficientes. Para experiências superiores e escalabilidade, chatbots inteligentes são o investimento certo.
                </p>
              </>
            )}

            {post.id === 4 && (
              <>
                <h2 className="text-3xl font-bold text-white mb-6">Análise de ROI em IA</h2>
                <p className="text-gray-300 mb-6">
                  Baseado em dados reais de mais de 50 empresas brasileiras que implementaram soluções de IA, apresentamos uma análise detalhada do retorno sobre investimento e os fatores que influenciam o sucesso.
                </p>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Investimento Inicial Médio</h3>
                <div className="grid sm:grid-cols-3 gap-6 mb-8">
                  <div className="bg-dark-secondary p-6 rounded-xl text-center">
                    <div className="text-3xl font-bold text-accent-green mb-2">R$ 15-30k</div>
                    <div className="text-gray-400">Pequenas Empresas</div>
                  </div>
                  <div className="bg-dark-secondary p-6 rounded-xl text-center">
                    <div className="text-3xl font-bold text-accent-green mb-2">R$ 50-150k</div>
                    <div className="text-gray-400">Médias Empresas</div>
                  </div>
                  <div className="bg-dark-secondary p-6 rounded-xl text-center">
                    <div className="text-3xl font-bold text-accent-green mb-2">R$ 200k+</div>
                    <div className="text-gray-400">Grandes Empresas</div>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Retornos Observados</h3>
                <div className="space-y-4 mb-8">
                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">Redução de Custos Operacionais</span>
                      <span className="text-accent-green text-2xl font-bold">45-65%</span>
                    </div>
                    <p className="text-gray-400 text-sm">Economia com equipe de atendimento e processos manuais</p>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">Aumento de Produtividade</span>
                      <span className="text-accent-green text-2xl font-bold">35-50%</span>
                    </div>
                    <p className="text-gray-400 text-sm">Equipe focada em tarefas estratégicas de alto valor</p>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">Crescimento de Receita</span>
                      <span className="text-accent-green text-2xl font-bold">20-40%</span>
                    </div>
                    <p className="text-gray-400 text-sm">Melhor conversão e capacidade de atender mais clientes</p>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">Satisfação do Cliente</span>
                      <span className="text-accent-green text-2xl font-bold">+85%</span>
                    </div>
                    <p className="text-gray-400 text-sm">NPS médio aumentou de 45 para 83 pontos</p>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Tempo para ROI Positivo</h3>
                <ul className="list-disc list-inside text-gray-300 mb-6 space-y-3">
                  <li><strong className="text-white">3-6 meses:</strong> Empresas com alto volume de atendimento</li>
                  <li><strong className="text-white">6-12 meses:</strong> Média das empresas analisadas</li>
                  <li><strong className="text-white">12-18 meses:</strong> Implementações complexas ou baixo volume</li>
                </ul>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Fatores de Sucesso</h3>
                <p className="text-gray-300 mb-4">
                  As empresas com melhor ROI compartilham estas características:
                </p>
                <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
                  <li>Objetivos claros e mensuráveis desde o início</li>
                  <li>Engajamento da liderança e equipe</li>
                  <li>Implementação gradual com aprendizado contínuo</li>
                  <li>Integração com sistemas existentes</li>
                  <li>Monitoramento constante de métricas</li>
                </ul>

                <div className="bg-gradient-to-r from-accent-green/10 to-transparent p-8 rounded-2xl mt-8 border-l-4 border-accent-green">
                  <h4 className="text-xl font-bold text-white mb-4">📊 Conclusão dos Dados</h4>
                  <p className="text-gray-300">
                    O ROI médio observado foi de 340% em 24 meses. Empresas que investem em IA não apenas recuperam o investimento rapidamente, mas estabelecem vantagem competitiva sustentável no mercado.
                  </p>
                </div>
              </>
            )}

            {post.id === 5 && (
              <>
                <h2 className="text-3xl font-bold text-white mb-6">As 7 Tendências que Definirão 2025</h2>
                <p className="text-gray-300 mb-6">
                  O cenário de inteligência artificial está evoluindo rapidamente. Estas são as tendências que toda empresa precisa conhecer para se manter competitiva em 2025.
                </p>

                <div className="space-y-8">
                  <div className="bg-dark-secondary p-8 rounded-2xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-accent-green/20 rounded-full flex-shrink-0">
                        <span className="text-2xl font-bold text-accent-green">1</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-3">IA Multimodal</h3>
                        <p className="text-gray-300 mb-4">
                          Sistemas que processam simultaneamente texto, voz, imagem e vídeo. Chatbots que entendem fotos de produtos, assistentes que analisam documentos visuais e muito mais.
                        </p>
                        <div className="text-sm text-accent-green font-medium">
                          Impacto: Alto | Adoção: Crescente
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-secondary p-8 rounded-2xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-accent-green/20 rounded-full flex-shrink-0">
                        <span className="text-2xl font-bold text-accent-green">2</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-3">Agentes Autônomos</h3>
                        <p className="text-gray-300 mb-4">
                          IA que não apenas responde, mas age. Agentes que agendam reuniões, processam pedidos, resolvem problemas e tomam decisões sem intervenção humana.
                        </p>
                        <div className="text-sm text-accent-green font-medium">
                          Impacto: Muito Alto | Adoção: Emergente
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-secondary p-8 rounded-2xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-accent-green/20 rounded-full flex-shrink-0">
                        <span className="text-2xl font-bold text-accent-green">3</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-3">IA Explicável e Transparente</h3>
                        <p className="text-gray-300 mb-4">
                          Sistemas que explicam suas decisões e raciocínio. Essencial para conformidade regulatória e confiança do cliente.
                        </p>
                        <div className="text-sm text-accent-green font-medium">
                          Impacto: Alto | Adoção: Necessária
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-secondary p-8 rounded-2xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-accent-green/20 rounded-full flex-shrink-0">
                        <span className="text-2xl font-bold text-accent-green">4</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-3">Personalização Hiperprecisa</h3>
                        <p className="text-gray-300 mb-4">
                          IA que adapta comunicação, ofertas e experiências em tempo real baseado em comportamento, preferências e contexto de cada cliente individual.
                        </p>
                        <div className="text-sm text-accent-green font-medium">
                          Impacto: Muito Alto | Adoção: Acelerada
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-secondary p-8 rounded-2xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-accent-green/20 rounded-full flex-shrink-0">
                        <span className="text-2xl font-bold text-accent-green">5</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-3">IA Edge Computing</h3>
                        <p className="text-gray-300 mb-4">
                          Processamento de IA diretamente em dispositivos locais, reduzindo latência e aumentando privacidade. Crucial para aplicações em tempo real.
                        </p>
                        <div className="text-sm text-accent-green font-medium">
                          Impacto: Médio | Adoção: Crescente
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-secondary p-8 rounded-2xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-accent-green/20 rounded-full flex-shrink-0">
                        <span className="text-2xl font-bold text-accent-green">6</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-3">IA Generativa para Dados</h3>
                        <p className="text-gray-300 mb-4">
                          Geração sintética de dados para treinamento, testes e análise. Resolve problemas de privacidade e escassez de dados.
                        </p>
                        <div className="text-sm text-accent-green font-medium">
                          Impacto: Alto | Adoção: Emergente
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-dark-secondary p-8 rounded-2xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-accent-green/20 rounded-full flex-shrink-0">
                        <span className="text-2xl font-bold text-accent-green">7</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-3">IA Sustentável</h3>
                        <p className="text-gray-300 mb-4">
                          Foco em eficiência energética e redução de pegada de carbono. Modelos menores e mais eficientes sem perda de performance.
                        </p>
                        <div className="text-sm text-accent-green font-medium">
                          Impacto: Médio | Adoção: Crescente
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-accent-green/10 to-transparent p-8 rounded-2xl mt-12 border-l-4 border-accent-green">
                  <h4 className="text-xl font-bold text-white mb-4">🚀 Prepare-se para o Futuro</h4>
                  <p className="text-gray-300">
                    Empresas que adotarem essas tendências cedo terão vantagem competitiva significativa. O momento de começar é agora.
                  </p>
                </div>
              </>
            )}

            {post.id === 6 && (
              <>
                <h2 className="text-3xl font-bold text-white mb-6">Integração WhatsApp + IA</h2>
                <p className="text-gray-300 mb-6">
                  O WhatsApp é o canal de comunicação preferido dos brasileiros. Aprenda a integrar um assistente de IA para automatizar seu atendimento 24/7 nesta plataforma.
                </p>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Por Que WhatsApp Business?</h3>
                <ul className="list-disc list-inside text-gray-300 mb-6 space-y-3">
                  <li>Mais de 165 milhões de usuários ativos no Brasil</li>
                  <li>Taxa de abertura de mensagens acima de 98%</li>
                  <li>Canal preferido para atendimento ao cliente</li>
                  <li>Suporte nativo a mídia, localização e documentos</li>
                  <li>API oficial para integração empresarial</li>
                </ul>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Pré-requisitos</h3>
                <div className="bg-dark-secondary p-6 rounded-xl mb-8">
                  <ul className="space-y-3 text-gray-300">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-accent-green mt-1"></i>
                      <span>Conta WhatsApp Business verificada</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-accent-green mt-1"></i>
                      <span>Acesso à API do WhatsApp Business (via Meta ou provedor)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-accent-green mt-1"></i>
                      <span>Plataforma de IA conversacional (como NeuroAgents)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-accent-green mt-1"></i>
                      <span>Webhook configurado para receber mensagens</span>
                    </li>
                  </ul>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Passo a Passo da Integração</h3>
                
                <div className="space-y-6 mb-8">
                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-accent-green mb-3">Passo 1: Configure o WhatsApp Business API</h4>
                    <p className="text-gray-300 mb-3">
                      Crie uma conta no Meta Business Manager e solicite acesso à API do WhatsApp Business. Você precisará de um número de telefone dedicado.
                    </p>
                    <div className="bg-black/30 p-4 rounded-lg">
                      <code className="text-sm text-gray-300">
                        Meta Business Manager → WhatsApp → Criar Conta Business
                      </code>
                    </div>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-accent-green mb-3">Passo 2: Configure Webhooks</h4>
                    <p className="text-gray-300 mb-3">
                      Configure um endpoint para receber mensagens dos clientes. Este endpoint processará as mensagens e enviará para a IA.
                    </p>
                    <div className="bg-black/30 p-4 rounded-lg overflow-x-auto">
                      <pre className="text-sm text-gray-300">
{`// Exemplo de webhook
app.post('/webhook', (req, res) => {
  const message = req.body.entry[0]
    .changes[0].value.messages[0];
  
  // Enviar para IA processar
  processWithAI(message);
  
  res.sendStatus(200);
});`}
                      </pre>
                    </div>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-accent-green mb-3">Passo 3: Integre com Plataforma de IA</h4>
                    <p className="text-gray-300 mb-3">
                      Conecte seu webhook à plataforma de IA conversacional. Configure o contexto, personalidade e conhecimento do assistente.
                    </p>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-accent-green mb-3">Passo 4: Configure Respostas Automáticas</h4>
                    <p className="text-gray-300 mb-3">
                      Defina mensagens de boas-vindas, horário de atendimento e respostas para perguntas frequentes.
                    </p>
                  </div>

                  <div className="bg-dark-secondary p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-accent-green mb-3">Passo 5: Teste e Ajuste</h4>
                    <p className="text-gray-300 mb-3">
                      Realize testes extensivos com diferentes tipos de perguntas. Monitore as conversas e ajuste as respostas da IA conforme necessário.
                    </p>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Funcionalidades Avançadas</h3>
                <ul className="list-disc list-inside text-gray-300 mb-6 space-y-3">
                  <li><strong className="text-white">Botões Interativos:</strong> Crie menus e opções clicáveis</li>
                  <li><strong className="text-white">Listas de Produtos:</strong> Mostre catálogo diretamente no chat</li>
                  <li><strong className="text-white">Mídia Rica:</strong> Envie imagens, vídeos e documentos</li>
                  <li><strong className="text-white">Localização:</strong> Compartilhe endereço da loja</li>
                  <li><strong className="text-white">Pagamentos:</strong> Integre checkout dentro do WhatsApp</li>
                </ul>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Melhores Práticas</h3>
                <div className="bg-gradient-to-r from-accent-green/10 to-transparent p-6 rounded-xl border-l-4 border-accent-green mb-8">
                  <ul className="space-y-3 text-gray-300">
                    <li>✓ Sempre identifique que é um assistente de IA</li>
                    <li>✓ Ofereça opção de falar com humano quando necessário</li>
                    <li>✓ Mantenha respostas concisas e objetivas</li>
                    <li>✓ Use emojis com moderação para humanizar</li>
                    <li>✓ Respeite horários e não envie spam</li>
                    <li>✓ Monitore métricas de satisfação constantemente</li>
                  </ul>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 mt-8">Resultados Esperados</h3>
                <p className="text-gray-300 mb-6">
                  Empresas que implementam IA no WhatsApp reportam redução de 70% no tempo de resposta, aumento de 45% na satisfação do cliente e capacidade de atender 10x mais conversas simultâneas.
                </p>
              </>
            )}
          </div>

          {/* Share Section */}
          <div className="mt-16 pt-8 border-t border-dark-border">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 font-medium">Compartilhe este artigo:</span>
              <div className="flex items-center gap-3">
                <button className="w-10 h-10 flex items-center justify-center bg-dark-secondary hover:bg-accent-green/20 text-gray-300 hover:text-accent-green rounded-full transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center bg-dark-secondary hover:bg-accent-green/20 text-gray-300 hover:text-accent-green rounded-full transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center bg-dark-secondary hover:bg-accent-green/20 text-gray-300 hover:text-accent-green rounded-full transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </button>
                <button className="w-10 h-10 flex items-center justify-center bg-dark-secondary hover:bg-accent-green/20 text-gray-300 hover:text-accent-green rounded-full transition-colors cursor-pointer">
                  <i className="ri-whatsapp-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl font-bold text-white mb-8">
              Artigos <span className="text-accent-green">Relacionados</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group bg-dark-secondary rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl hover:shadow-accent-green/5 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-secondary to-transparent opacity-60" />
                    <div className="absolute top-4 left-4">
                      <span className={`${relatedPost.categoryColor} text-black px-3 py-1 rounded-full text-xs font-bold`}>
                        {relatedPost.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-accent-green transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <BlogFooter />
    </div>
  );
}
